import React from "react";
import styled from "styled-components";
import { Button } from "@shared/ui/Button";
import { Input } from "@shared/ui/Input";
import { Controller, useForm } from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { useTranslation } from "next-i18next";
import { HorizontalFormField } from "@shared/ui/HorizontalFormField";
import { PageBackButton } from "@shared/ui/PageBackButton";
import { CodeTimer } from "@features/CodeTimer";
import { IApplicationFormStep1Code } from "@entities/Product/Application/types";
import { useUnit } from "effector-react";
import { confirmPhoneFx } from "@shared/model";
import {
  $applicationFormStep1,
  $applicationFormStep1Code,
  $applicationStep,
  resendVerificationCode,
  setApplicationStep,
  submitApplicationStep1Code,
} from "@entities/Product/Application/model";
import { zodResolver } from "@hookform/resolvers/zod";
import { applicationValidationStep1CodeSchema } from "@entities/Product/Application/utils";

export function ApplicationStep1Code() {
  const { t } = useTranslation();
  const isLoadingConfirmPhone = useUnit(confirmPhoneFx.pending);
  const [step1CodeData, submitStep] = useUnit([
    $applicationFormStep1Code,
    submitApplicationStep1Code,
  ]);
  const step1Data = useUnit($applicationFormStep1);
  const confirmPhone = useUnit(confirmPhoneFx);
  const resendCode = useUnit(resendVerificationCode);
  const [activeStep, setActiveStep] = useUnit([
    $applicationStep,
    setApplicationStep,
  ]);

  const methods = useForm<IApplicationFormStep1Code>({
    mode: "onChange",
    shouldUnregister: false,
    resolver: zodResolver(applicationValidationStep1CodeSchema(t)),
    defaultValues: step1CodeData,
  });
  const resendCodeHandler = async () => {
    try {
      await resendCode({ phone: step1Data.phone.replace(/[^0-9]/g, "") });
      methods.setValue("verification_code", "");
      methods.clearErrors("verification_code");
    } catch (e) {
      if (e.response.status === 429) {
        methods.setError("verification_code", {
          message: t("Лимит попыток превышен, попробуйте через 30 минут"),
        });
      }
    }
  };

  const onSubmit = methods.handleSubmit(async (data) => {
    try {
      submitStep(data);
      await confirmPhone({
        phone: step1Data.phone.replace(/[^0-9]/g, ""),
        code: data.verification_code,
      });
      setActiveStep(activeStep + 1);
    } catch (e) {
      methods.setError("verification_code", {
        message: t(`Неверный SMS-код`),
      });
    }
  });

  const goBack = () => {
    setActiveStep(activeStep - 1);
    methods.setValue("verification_code", "");
  };

  return (
    <StyledWrapper onSubmit={onSubmit}>
      <HorizontalFormField label={t("SMS-код")}>
        <Controller
          name="verification_code"
          control={methods.control}
          render={({ field, fieldState }) => (
            <>
              <PatternFormat
                {...field}
                hasError={Boolean(fieldState.error?.message)}
                errorMessage={fieldState.error?.message}
                format="####"
                mask="-"
                type="text"
                autoComplete="new-password"
                customInput={Input}
              />
              <StyledCodeTimer sendCode={resendCodeHandler} />
            </>
          )}
        />
      </HorizontalFormField>
      <StyledBottom>
        <StyledPageBackButton
          onClick={goBack}
          color="primary_red"
          label={t(`Вернуться назад`)}
        />
        <Button
          type="submit"
          disabled={!methods.formState.isValid || isLoadingConfirmPhone}
        >
          {t(`Подтвердить`)}
        </Button>
      </StyledBottom>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.form`
  display: grid;
  grid-gap: 2.4rem;
`;

const StyledCodeTimer = styled(CodeTimer)`
  margin-top: 2.4rem;
  text-align: center;
  justify-content: center;
`;

const StyledBottom = styled.div`
  display: grid;
  margin-top: 1.6rem;
  grid-template-columns: 1.2fr 1.8fr;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-template-columns: 1fr;
    grid-gap: 3.2rem;
  }
`;

const StyledPageBackButton = styled(PageBackButton)`
  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    width: fit-content;
  }
`;
